import { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsInstance } from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { registerVerticeThemes, resolveThemeName } from '@/lib/charts/theme';
import { animationDefaults } from '@/lib/charts/animations';
import { cn } from '@/lib/utils';

registerVerticeThemes();

type EChartEventHandler = (params: unknown, instance: EChartsInstance) => void;

interface EChartProps {
  option: EChartsOption;
  className?: string;
  style?: React.CSSProperties;
  loading?: boolean;
  notMerge?: boolean;
  lazyUpdate?: boolean;
  onEvents?: Record<string, EChartEventHandler>;
  onReady?: (instance: EChartsInstance) => void;
}

function useResolvedTheme(): string {
  const [theme, setTheme] = useState<string>(() => resolveThemeName());

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      // `data-theme` is already flipped here; re-register so the light theme's token snapshot
      // (text/axis/grid) reflects the now-active theme before the new theme is applied — fixes a
      // boot-in-dark → switch-to-light session reusing a stale (dark-valued) light theme.
      registerVerticeThemes();
      const next = resolveThemeName();
      setTheme((prev) => (prev === next ? prev : next));
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return theme;
}

export function EChart({
  option,
  className,
  style,
  loading,
  notMerge = false,
  lazyUpdate = true,
  onEvents,
  onReady,
}: EChartProps) {
  const theme = useResolvedTheme();
  const ref = useRef<ReactECharts | null>(null);

  const mergedOption = useMemo<EChartsOption>(
    () => ({ ...animationDefaults, ...option }),
    [option],
  );

  useEffect(() => {
    if (ref.current && onReady) {
      onReady(ref.current.getEchartsInstance());
    }
  }, [onReady]);

  useEffect(() => {
    const chart = ref.current?.getEchartsInstance();
    if (!chart || typeof ResizeObserver === 'undefined') return;

    // A theme flip disposes and recreates the ECharts instance; getDom() is null on a
    // disposed instance, so guard before touching the DOM (this effect re-runs on `theme`).
    const dom = chart.getDom();
    if (!dom) return;
    const target = dom.parentElement ?? dom;
    // Skip resizing when the container is collapsed (e.g. an inactive, display:none tab):
    // resizing to 0 then back on re-show causes a visible flicker on tab switch. Keeping the
    // last good size means the chart is already correctly laid out the instant the tab appears.
    const resize = () => {
      const rect = target.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) chart.resize();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(target);
    const frame = window.requestAnimationFrame(resize);
    const timeout = window.setTimeout(resize, 120);

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [theme]);

  useEffect(() => {
    const chart = ref.current?.getEchartsInstance();
    if (!chart) return;
    // Same 0×0 guard as the ResizeObserver effect: a data update while this chart sits in a
    // collapsed (display:none) tab would otherwise resize it to 0 and flicker on re-show.
    const frame = window.requestAnimationFrame(() => {
      const dom = chart.getDom();
      if (!dom) return; // instance disposed (e.g. a theme flip) before the frame fired
      const target = dom.parentElement ?? dom;
      const rect = target.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) chart.resize();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [mergedOption]);

  return (
    <ReactECharts
      ref={(instance) => {
        ref.current = instance;
      }}
      option={mergedOption}
      theme={theme}
      notMerge={notMerge}
      lazyUpdate={lazyUpdate}
      showLoading={loading}
      onEvents={onEvents}
      style={{ width: '100%', height: '100%', ...style }}
      className={cn(className)}
      opts={{ renderer: 'canvas' }}
    />
  );
}

export type { EChartsOption };
